import { useEffect, useContext, MouseEvent, useRef } from "react";
import { Card, Box, Typography } from "@mui/material";
import { useNodeId, NodeProps } from "@xyflow/react";
import orgChartContext from "src/Pages/Company/OrganizationStructure/context/OrgchartContext";
import __ from "src/utils/Translations";
import { useInView, motion } from "motion/react";
import { Avatar, PresenceIndicator } from "@benefit-systems/hr-tools-ui";
import { Link } from "react-router-dom";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useFormContext } from "react-hook-form";
import { TAreaNode } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/customNodes.models";
import BottomHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/shared-components/BottomHandle";
import TopHandle from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/CustomReactFlow/Nodes/shared-components/TopHandle";
import useOrgchartAreasStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Areas/store";
import useSearchedNodesStyles from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Areas/useSearchedNodesStyles";

const Node = ({ data, id: nodeId }: NodeProps<TAreaNode>) => {
  const isAhr = useIsAhr();
  const ref = useRef(null);
  const isInView = useInView(ref);
  const { resetField } = useFormContext();
  const { edges } = useOrgchartAreasStore();
  const { changeNodesVisibility } = useContext(orgChartContext);
  const clickedNodeId = useNodeId() || "";
  const setNodeToFocusOn = useOrgchartAreasStore(
    (state) => state.setNodeToFocusOn
  );
  const setAreaDataForDialog = useOrgchartAreasStore(
    (state) => state.setAreaDataForDialog
  );
  const setIsDialogOpen = useOrgchartAreasStore(
    (state) => state.setIsDialogOpen
  );
  const childrenCollapsedStatus = useOrgchartAreasStore(
    (state) => state.childrenCollapsedStatus
  );
  const isClickedNodeCollapsed = childrenCollapsedStatus.get(nodeId);
  const companyId = useCompanyId();

  const searchedStyles = useSearchedNodesStyles(nodeId);
  const setSearchedNodeId = useOrgchartAreasStore(
    (state) => state.setSearchedNodeId
  );

  const stopPropagation = (event: MouseEvent) => {
    event.stopPropagation();
    setSearchedNodeId(null);
  };

  const handleClick = () => {
    if (typeof isClickedNodeCollapsed !== "undefined") {
      useOrgchartAreasStore.getState().setSearchedNodeId(null);
      useOrgchartAreasStore.getState().setWasSearchedNodeCleared(true);
      resetField("autocomplete", { defaultValue: "" });
      useOrgchartAreasStore.getState().toggleChildrenCollapsedStatus(nodeId);
      setNodeToFocusOn({
        id: nodeId,
        isCollapsed: !isClickedNodeCollapsed,
      });
      changeNodesVisibility({
        isClickedNodeCollapsed,
        clickedNodeId: nodeId,
        edges,
      });
    }
  };

  useEffect(() => {
    useOrgchartAreasStore.getState().updateVisibleNodeRef(nodeId, isInView);
  }, [isInView, nodeId]);

  const handleDisplayAreaMembers = () => {
    setAreaDataForDialog({ id: clickedNodeId, name: data.name });
    setIsDialogOpen(true);
  };

  return (
    <div ref={ref} style={{ opacity: searchedStyles.opacity }}>
      <TopHandle />
      <motion.div
        layout
        layoutId={`layoutFor${clickedNodeId}`}
        transition={{ duration: 0.2 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.2, delay: 0.4 } }}
        onClick={stopPropagation}
      >
        <Card
          variant="elevation"
          elevation={3}
          sx={{
            borderRadius: "12px",
            padding: "0",
            width: "340px",
            minWidth: "340px",
            maxWidth: "340px",
            border: searchedStyles.border,
            cursor: "default",
          }}
        >
          <Box sx={{ background: "#E5F6FD", padding: "12px 16px 12px 24px" }}>
            <Link
              to={
                isAhr
                  ? `/ahr/organization-structure/configure-structure/area/${nodeId}`
                  : `/company/edit/${companyId}/organization-structure/configure-structure/area/${nodeId}`
              }
              style={{
                textDecoration: "none",
              }}
            >
              <Typography
                sx={{ fontWeight: "fontWeightMedium", color: "text.primary" }}
              >
                {data.name}
              </Typography>
            </Link>
          </Box>
          <Box sx={{ padding: "12px 16px" }}>
            {data.manager?.employeeId && (
              <>
                <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
                  {__("Zarządzający obszarem")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 0 12px 0",
                  }}
                >
                  {(data.manager?.avatar || data.manager?.initials) &&
                    (data.manager?.employeeId ? (
                      <Link
                        to={
                          isAhr
                            ? `/ahr/employees/${data.manager.employeeId}`
                            : `/company/edit/${companyId}/employee-management/employees/${data.manager.employeeId}`
                        }
                        style={{ textDecoration: "none" }}
                      >
                        {data.manager.presenceInformation ? (
                          <PresenceIndicator
                            status={data.manager?.presenceInformation?.status}
                          >
                            <Avatar size={13} src={data.manager?.avatar}>
                              {data.manager?.initials || ""}
                            </Avatar>
                          </PresenceIndicator>
                        ) : (
                          <Avatar size={13} src={data.manager?.avatar}>
                            {data.manager?.initials || ""}
                          </Avatar>
                        )}
                      </Link>
                    ) : null)}
                  <Box>
                    <Link
                      to={
                        isAhr
                          ? `/ahr/employees/${data.manager.employeeId}`
                          : `/company/edit/${companyId}/employee-management/employees/${data.manager.employeeId}`
                      }
                      style={{
                        textDecoration: "none",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: "fontWeightMedium",
                          color: "text.primary",
                        }}
                      >
                        {__(data.manager?.name)}
                      </Typography>
                    </Link>
                    {data.manager?.fk && (
                      <Typography
                        sx={{ color: "text.secondary" }}
                        variant="body2"
                      >
                        {`(FK ${data.manager.fk})`}
                      </Typography>
                    )}
                    {data.manager?.position && (
                      <Typography
                        sx={{ color: "text.secondary" }}
                        variant="body2"
                      >
                        {__(data.manager.position)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </>
            )}
            {data.membersNumber !== 0 && data.previewMembers.length !== 0 && (
              <>
                {data.manager.employeeId && (
                  <Box
                    sx={{
                      width: "70%",
                      height: "1px",
                      margin: "0 auto",
                      backgroundColor: "#EBF2F5",
                    }}
                  />
                )}
                <Box
                  sx={{
                    display: "flex",
                    padding: "16px 0",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "160px",
                      height: "32px",
                    }}
                  >
                    {data.previewMembers.map((member, index) => (
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${index === 0 ? 0 : index * 32 - 3 * index}px`,
                          zIndex: data.previewMembers.length - index,
                          border: "2px solid white",
                          borderRadius: "100%",
                        }}
                        key={`member_${member.employeeId}`}
                      >
                        <Avatar size={8} src={member.avatar}>
                          {member.initials}
                        </Avatar>
                      </Box>
                    ))}
                    {data.previewMembers.length < data.membersNumber && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${
                            data.previewMembers.length * 32 -
                            3 * data.previewMembers.length
                          }px`,
                          zIndex: 0,
                          border: "2px solid white",
                          borderRadius: "100%",
                        }}
                      >
                        <Avatar size={8}>{`+${
                          data.membersNumber - data.previewMembers.length
                        }`}</Avatar>
                      </Box>
                    )}
                  </Box>
                  <Typography
                    onClick={handleDisplayAreaMembers}
                    sx={{
                      textDecoration: "underline",
                      "&:hover": { cursor: "pointer" },
                    }}
                  >
                    {__("Pokaż członków")}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Card>
      </motion.div>
      {data.hasChildren && typeof isClickedNodeCollapsed !== "undefined" ? (
        <BottomHandle
          handleClick={handleClick}
          isClickedNodeCollapsed={isClickedNodeCollapsed}
        />
      ) : null}
    </div>
  );
};

export default Node;
